import axios from "axios";

import {
  configIdFromPreviewUrl,
  reportConfigIdFromArtifact,
} from "@/components/artifacts/artifactReportViewer";
import { fetchArtifactPdfBlob, getArtifact } from "@/services/api/agentic-v2";
import type { ArtefactReviewItem } from "./types";

export type AdviceDeckPreviewResult =
  | { type: "pdf"; blob: Blob }
  | { type: "iframe"; url: string; notice: string };

type AdviceDeckReviewContext = Pick<
  ArtefactReviewItem,
  "report_config_id" | "viewer_url" | "preview_path"
>;

function reportBuilderDeckPreviewUrl(configId: string): string {
  return `/pdf?template=report-builder&config=${encodeURIComponent(configId)}`;
}

function shouldTryReportBuilderFallback(err: unknown): boolean {
  // Download returns 400 when the artefact has no Azure-backed bytes (common in local dev).
  return axios.isAxiosError(err) && err.response?.status === 400;
}

function reportConfigIdFromReviewItem(
  artefact?: AdviceDeckReviewContext | string | null,
): string | null {
  if (typeof artefact === "string") {
    return artefact.trim() || null;
  }
  if (!artefact) return null;
  return (
    artefact.report_config_id?.trim()
    ?? configIdFromPreviewUrl(artefact.viewer_url)
    ?? configIdFromPreviewUrl(artefact.preview_path)
    ?? null
  );
}

async function resolveReportConfigId(
  artifactId: string,
  artefact?: AdviceDeckReviewContext | string | null,
): Promise<string | null> {
  const fromReview = reportConfigIdFromReviewItem(artefact);
  if (fromReview) return fromReview;
  const artifact = await getArtifact(artifactId);
  return reportConfigIdFromArtifact(artifact);
}

/**
 * Resolve how to preview an advice pack for review.
 *
 * 1. Azure-backed PDF bytes when the worker stored a file (production)
 * 2. Report-builder iframe when the deck was rendered but no PDF was stored
 *    (same surface as “View deck” / “Open in editor” in the agent message)
 */
export async function resolveAdviceDeckPreview(
  artifactId: string,
  artefact?: AdviceDeckReviewContext | string | null,
): Promise<AdviceDeckPreviewResult> {
  try {
    const blob = await fetchArtifactPdfBlob(artifactId);
    return { type: "pdf", blob };
  } catch (err) {
    if (!shouldTryReportBuilderFallback(err)) throw err;
    const configId = await resolveReportConfigId(artifactId, artefact);
    if (!configId) {
      throw new Error("Advice pack preview is not available — no stored PDF or report config.");
    }
    return {
      type: "iframe",
      url: reportBuilderDeckPreviewUrl(configId),
      notice: "PDF file was not stored for this run — showing the rendered deck preview.",
    };
  }
}

/** Open the advice pack preview in a new tab. */
export async function openAdviceDeckPdfPreview(
  artifactId: string,
  artefact?: AdviceDeckReviewContext | string | null,
): Promise<void> {
  const preview = await resolveAdviceDeckPreview(artifactId, artefact);
  if (preview.type === "iframe") {
    const opened = window.open(preview.url, "_blank", "noopener,noreferrer");
    if (!opened) {
      throw new Error("Popup blocked — allow popups to open the deck preview.");
    }
    return;
  }

  const objectUrl = URL.createObjectURL(preview.blob);
  const opened = window.open(objectUrl, "_blank", "noopener,noreferrer");
  if (!opened) {
    URL.revokeObjectURL(objectUrl);
    throw new Error("Popup blocked — allow popups to open the PDF preview.");
  }
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
